// ===========================================
// DELIVERY ECONOMICS — what each drop costs and earns
// ===========================================
// Per-account and per-week rollup of delivery costs from
// /wholesale/summary/delivery. Only orders with a delivery cost recorded
// count as drops; margin per drop = items revenue − inventory item costs
// (matched by product name/SKU) − delivery cost, and stays blank when any
// line couldn't be matched — a half-true number helps nobody.
// Account rows stay in the account's own currency; combined totals convert at
// the day's ECB rate (dated below) — or show per-currency when no rate exists.

import { useQuery } from '@tanstack/react-query';
import { Truck } from 'lucide-react';
import clsx from 'clsx';
import { wholesaleApi } from '../services/api';
import { formatMoney, formatByCurrency } from '../lib/money';
import type { EntityView } from './EntityToggle';

const dollars = (c: number | null | undefined, cur = 'USD') => formatMoney(c, cur);
const exact = (c: number | null | undefined, cur = 'USD') => formatMoney(c, cur, { exact: true });
const weekLabel = (iso: string) =>
  `wk of ${new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

export default function DeliveryEconomics({ entity = 'all' }: { entity?: EntityView }) {
  const { data, isPending } = useQuery({
    queryKey: ['wholesale-delivery-summary', entity],
    queryFn: async () => (await wholesaleApi.getDeliverySummary(entity === 'all' ? undefined : entity)).data?.data || null,
    staleTime: 60_000,
  });
  if (isPending || !data) return null;

  // Nothing tracked yet: say how to start instead of showing a dead table.
  if (!data.totals || data.totals.drops === 0) {
    return (
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Truck className="w-4 h-4 text-primary-600" />
          <h2 className="font-semibold text-slate-900">Delivery cost per drop</h2>
        </div>
        <p className="text-sm text-slate-500">
          Add a delivery cost to any order (edit the order, "Delivery cost $") and the per-drop economics — cost, revenue and margin per account and per week — appear here.
        </p>
      </div>
    );
  }

  const t = data.totals;
  const marginCell = (r: any) => r.marginCents == null
    ? <span className="text-slate-300" title={r.unmatchedLines
        ? `${r.unmatchedLines} line${r.unmatchedLines === 1 ? '' : 's'} didn't match an inventory item`
        : r.currency && r.currency !== 'USD' ? 'Inventory costs are in USD — margin stays blank rather than mixing currencies' : undefined}>—</span>
    : <span className={clsx('font-medium', r.marginCents >= 0 ? 'text-slate-900' : 'text-rose-600')}>{dollars(r.marginCents)}</span>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="card p-3"><p className="text-xs text-slate-500">Drops tracked</p><p className="text-2xl font-bold text-slate-900">{t.drops}</p></div>
        <div className="card p-3"><p className="text-xs text-slate-500">Delivery spend</p><p className="text-2xl font-bold text-slate-900">{t.deliveryCents == null ? formatByCurrency(t.deliveryByCurrency) : dollars(t.deliveryCents)}</p></div>
        <div className="card p-3"><p className="text-xs text-slate-500">Avg per drop</p><p className="text-2xl font-bold text-slate-900">{t.avgDeliveryCents == null ? '—' : exact(t.avgDeliveryCents)}</p></div>
        <div className="card p-3"><p className="text-xs text-slate-500">Margin after delivery</p><p className="text-2xl font-bold text-slate-900">{t.marginCents == null ? '—' : dollars(t.marginCents)}</p>
          {t.unmatchedLines > 0 && <p className="text-[11px] text-amber-600">{t.unmatchedLines} line{t.unmatchedLines === 1 ? '' : 's'} missing an inventory cost</p>}</div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium">Account</th>
              <th className="px-4 py-2 font-medium text-right">Drops</th>
              <th className="px-4 py-2 font-medium text-right">Delivery</th>
              <th className="px-4 py-2 font-medium text-right">Avg / drop</th>
              <th className="px-4 py-2 font-medium text-right">Revenue</th>
              <th className="px-4 py-2 font-medium text-right">Margin / period</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {(data.accounts || []).map((r: any) => (
              <tr key={r.customerId}>
                <td className="px-4 py-2.5 font-medium text-slate-900">{r.customerName}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{r.drops}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{dollars(r.deliveryCents, r.currency)}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{exact(r.avgDeliveryCents, r.currency)}</td>
                <td className="px-4 py-2.5 text-right text-slate-600">{dollars(r.revenueCents, r.currency)}</td>
                <td className="px-4 py-2.5 text-right">{marginCell(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(data.weeks || []).length > 0 && (
        <div className="card p-4">
          <p className="text-sm font-medium text-slate-700 mb-2">Week by week</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-1.5">
            {[...data.weeks].reverse().map((w: any) => (
              <div key={w.week} className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5 text-center">
                <p className="text-[10px] text-slate-400">{weekLabel(w.week)}</p>
                <p className="text-xs font-semibold text-slate-900">{w.deliveryCents == null ? formatByCurrency(w.deliveryByCurrency) : dollars(w.deliveryCents)} <span className="font-normal text-slate-400">· {w.drops} drop{w.drops === 1 ? '' : 's'}</span></p>
                <p className="text-[10px] text-slate-400">{w.marginCents == null ? 'margin —' : `${dollars(w.marginCents)} mgn`}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.fx?.label && <p className="text-[11px] text-slate-400">{data.fx.label}</p>}
      <p className="text-[11px] text-slate-400">{data.source}</p>
    </div>
  );
}
